<script>
export default {
	name: 'DeleteConfirmationPopup',
	inheritAttrs: false,
};
</script>

<script setup>
import CTAButton from '~/aem-components/CTAButton.vue';

const props = defineProps({
	isVisible: { type: Boolean, default: false },
	cancelCtaLabel: { type: String, default: 'Cancel' },
	deleteCtaLabel: { type: String, default: 'Yes' },
	deleteListTitleText: { type: String, default: 'Remove list' },
	deleteMessageText: { type: String, default: '' },
});

const emit = defineEmits([
	'update:isVisible',
	'click-cancel',
	'click-delete',
]);

const internalVisible = ref(props.isVisible);
const rootEl = ref(null);

watch(internalVisible, (newValue) => {
	emit('update:isVisible', newValue);
});
</script>


<template>
<Teleport to="body">
	<Transition
		name="overlay-fade-and-zoom"
		appear
	>
		<div
			v-if="props.isVisible"
			ref="rootEl"
			class="DeleteConfirmationPopup backdrop"
			v-bind="$attrs"
			tabindex="-1"
		>
			<div class="overlay-container">
				<div class="overlay-header >lg:hidden">
					<button
						type="button"
						class="absolute top-4 right-4"
						aria-label="Popup close button"
						@click="internalVisible = false"
					>
						<icon-far-xmark class="w-6 h-6 fill-primary-black-base" />
					</button>
				</div>
				<div class="overlay-body px-6">
					<h4 class="text-center">{{ props.deleteListTitleText }}</h4>
					<div class="text-center mt-6">{{ props.deleteMessageText }}</div>
				</div>
				<div class="overlay-footer">
					<CTAButton
						:ctaText="props.cancelCtaLabel"
						sizeType="small"
						class="font-semibold"
						styleType="secondary"
						@click="emit('click-cancel')"
					/>
					<CTAButton
						:ctaText="props.deleteCtaLabel"
						sizeType="small"
						class="font-semibold"
						styleType="primary"
						@click="emit('click-delete')"
					/>
				</div>
			</div>
		</div>
	</Transition>
</Teleport>
</template>


<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.backdrop {
	position: fixed;
	z-index: 10000;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: color.change(black, $alpha: 0.8);
	
	display: flex;
	justify-content: flex-end;
	transition: opacity 0.45s ease;
}

.overlay-container {
	background-color: var(--overlay-container-bg-color, white);
	max-width: 552px;
	width: 100%;
	margin: auto;
	overflow: hidden;
	@apply rounded-3xl;
	
	@media #{var.$query-max-md} {
		max-width: 375px;
	}
}

.overlay-header {
	position: relative;
}
.overlay-header-image {
	aspect-ratio: 3/2;
}

.overlay-body {
	padding: 44px 24px 36px 24px;
}

.overlay-footer {
	display: flex;
	align-items: center;
	justify-content: center;
	@apply gap-6;
	border-top: 1px solid var(--neutral-grey-base);
	padding: 36px 24px 44px 24px;
	
	@media #{var.$query-max-md} {
		@apply gap-4;
	}
}

// animations
.overlay-fade-and-zoom-enter-from,
.overlay-fade-and-zoom-leave-to {
	opacity: 0;
}

.overlay-fade-and-zoom-enter-active,
.overlay-fade-and-zoom-leave-active {
	transition: opacity 200ms;
}

.overlay-fade-and-zoom-enter-from,
.overlay-fade-and-zoom-leave-to {
	.overlay-container {
		transform: scale(1.2);
	}
}

.overlay-fade-and-zoom-enter-active,
.overlay-fade-and-zoom-leave-active {
	.overlay-container {
		transition: transform 200ms;
	}
}
</style>